import React, { useState, useEffect } from 'react';
import { StyleSheet } from 'react-native';

interface TableActionsPopupProps {
    onClose: () => void;
    onAddDiscount: () => void;
    onChangeTable: () => void;
    onMergeTables: () => void;
    onDeleteOrder: () => void;
    hasPerm?: (key: string) => boolean;
}


const TableActionsPopup: React.FC<TableActionsPopupProps> = ({ onClose, onAddDiscount, onChangeTable, onMergeTables, onDeleteOrder, hasPerm }) => {
    const can = (key: string) => !hasPerm || hasPerm(key);

    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const updateSize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", updateSize);
        window.addEventListener("focus", updateSize);
        document.addEventListener("visibilitychange", updateSize);

        return () => {
            window.removeEventListener("resize", updateSize);
            window.removeEventListener("focus", updateSize);
            document.removeEventListener("visibilitychange", updateSize);
        };
    }, []);

    const styles = StyleSheet.create({
        popup: {
            width: windowSize.width > 900 ? windowSize.width * 0.40 : windowSize.width * 0.9,
            backgroundColor: '#fff',
            position: 'absolute',
            zIndex: 1000,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
        },
    });

    return (
        <div style={styles.popup} className='ds-pop ds-pop--center'>
            <div className='d-flex px-3 pt-3 justify-content-end'>
                <button className='text-btn p-0 m-0 d-flex justify-content-center' onClick={onClose}>
                    <svg width="10" height="10" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 1L1 13M1 1L13 13" stroke="#98A2B3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
            </div>

            <div className='px-4 pb-3'>
                <h5>Table Actions</h5>
                <p>Apply Table Actions to Open orders.</p>

                {can('pos-app.order.move') && (
                <div className='border-box gap-2 p-2 mt-2 d-flex justify-content-between'>
                    <div className='col-auto'>
                        <svg width="34" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2 10C2 10 2.12132 9.15076 5.63604 5.63604C9.15076 2.12132 14.8492 2.12132 18.364 5.63604C19.6092 6.88131 20.4133 8.40072 20.7762 10M2 10V4M2 10H8M22 14C22 14 21.8787 14.8492 18.364 18.364C14.8492 21.8787 9.15076 21.8787 5.63604 18.364C4.39076 17.1187 3.58669 15.5993 3.22383 14M22 14V20M22 14H16" stroke="#475467" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </div>
                    <div className='col'>
                        <div className='d-flex flex-column'>
                            <label style={{ fontWeight: '500', fontSize: '14px' }} className='d-flex align-items-center'>Change Table</label>
                            <small style={{ color: '#6B7280' }}>If the guest changes table you can change the table number in the system.</small>
                        </div>
                    </div>
                    <div className='col-auto'>
                        <button className='blue-filled-btn mt-2' onClick={onChangeTable}>Select</button>
                    </div>
                </div>
                )}

                {can('pos-app.order.merge') && (
                <div className='border-box gap-2 p-2 mt-2 d-flex justify-content-between'>
                    <div className='col-auto'>
                        <svg width="34" height="12" viewBox="0 0 22 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.5 1H6C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11H8C10.7614 11 13 8.76142 13 6M15.5 11H16C18.7614 11 21 8.76143 21 6C21 3.23858 18.7614 1 16 1H14C11.2386 1 9 3.23858 9 6" stroke="#475467" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </div>
                    <div className='col'>
                        <div className='d-flex flex-column'>
                            <label style={{ fontWeight: '500', fontSize: '14px' }} className='d-flex align-items-center'>Merge Tables</label>
                            <small style={{ color: '#6B7280' }}>Merge two or more tables together into one and linked order.</small>
                        </div>
                    </div>
                    <div className='col-auto'>
                        <button className='blue-filled-btn mt-2' onClick={onMergeTables}>Select</button>
                    </div>
                </div>
                )}

                {can('pos-app.discount.apply') && (
                <div className='border-box gap-2 p-2 mt-2 d-flex justify-content-between'>
                    <div className='col-auto'>
                        <svg width="34" height="28" viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 13H9.01M15 19H15.01M16 12L8 20M9.5 13C9.5 13.2761 9.27614 13.5 9 13.5C8.72386 13.5 8.5 13.2761 8.5 13C8.5 12.7239 8.72386 12.5 9 12.5C9.27614 12.5 9.5 12.7239 9.5 13ZM15.5 19C15.5 19.2761 15.2761 19.5 15 19.5C14.7239 19.5 14.5 19.2761 14.5 19C14.5 18.7239 14.7239 18.5 15 18.5C15.2761 18.5 15.5 18.7239 15.5 19ZM22 16C22 21.5228 17.5228 26 12 26C6.47715 26 2 21.5228 2 16C2 10.4772 6.47715 6 12 6C17.5228 6 22 10.4772 22 16Z" stroke="#475467" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </div>
                    <div className='col'>
                        <div className='d-flex flex-column'>
                            <label style={{ fontWeight: '500', fontSize: '14px' }} className='d-flex align-items-center'>Add Discount</label>
                            <small style={{ color: '#6B7280' }}>Add discount in amount or percentage to a product or the entire table order.</small>
                        </div>
                    </div>
                    <div className='col-auto'>
                        <button className='blue-filled-btn mt-2' onClick={onAddDiscount}>Select</button>
                    </div>
                </div>
                )}

                {can('pos-app.order.delete') && (
                <div className='border-box gap-2 p-2 mt-2 d-flex justify-content-between' style={{ borderColor: '#F87171' }}>
                    <div className='col-auto'>
                        <svg width="34" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 3H15M3 6H21M19 6L18.2987 16.5193C18.1935 18.0975 18.1409 18.8867 17.8 19.485C17.4999 20.0118 17.0472 20.4353 16.5017 20.6997C15.882 21 15.0911 21 13.5093 21H10.4907C8.90891 21 8.11803 21 7.49834 20.6997C6.95276 20.4353 6.50009 20.0118 6.19998 19.485C5.85911 18.8867 5.8065 18.0975 5.70129 16.5193L5 6M10 10.5V15.5M14 10.5V15.5" stroke="#DC2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </div>
                    <div className='col'>
                        <div className='d-flex flex-column'>
                            <label style={{ fontWeight: '500', fontSize: '14px', color: '#DC2626' }} className='d-flex align-items-center'>Delete Order</label>
                            <small style={{ color: '#6B7280' }}>Permanently delete this order from the table.</small>
                        </div>
                    </div>
                    <div className='col-auto'>
                        <button className='mt-2' style={{ backgroundColor: '#DC2626', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }} onClick={onDeleteOrder}>Delete</button>
                    </div>
                </div>
                )}
            </div>
            <div className='p-2 d-flex justify-content-end' style={{ backgroundColor: '#F3F4F6' }}>
                <button className='white-filled-btn ' onClick={onClose}>Cancel</button>
            </div>
        </div>
    );
};

export default TableActionsPopup;
